/**
 * korsha-collection.js — in-place filtering, sorting and "load more" for the
 * KORSHA collection and search pages.
 *
 * The page already works without this file: filters and sort are GET forms
 * over Shopify's own parameters, and pagination is real links. This upgrades
 * them so the grid updates without a reload:
 *
 *   - a filter or sort change becomes the new URL (pushState, so refresh,
 *     share and Back all work) and the section is re-rendered through the
 *     Section Rendering API — Shopify does the filtering, not the browser
 *   - "Load more" appends the next page's cards; in "load on scroll" mode the
 *     same request fires as the button nears the viewport
 *
 * On phones and tablets the filter panel can gather choices instead
 * (`.korsha-filters--staged`): ticking a value only updates the "Apply
 * filters · 3" button, and Apply renders once, closes the panel and brings
 * the shopper back to the results. Closing without applying puts the form
 * back as it was. Desktop keeps filtering on every change.
 *
 * Focus and open filter groups are preserved across re-renders so keyboard
 * and screen reader users do not lose their place. The platform's
 * CollectionUpdateEvent (or SearchUpdateEvent on search) is dispatched, as
 * Horizon's facets did, so analytics that listen for it keep working.
 */

const eventsModule = import('@shopify/events').catch(() => null);
import('@theme/view-event-elements').catch(() => {});

const REGIONS = ['filter-count', 'count', 'summary', 'active', 'results', 'facets-sidebar', 'facets-drawer', 'drawer-foot', 'sort-hidden'];
const PRICE_DEBOUNCE = 700;
// Below this width the filter panel covers the grid, so choices are gathered
// and applied together; at and above it the grid stays in view and updates live.
const STAGED = window.matchMedia('(max-width: 989px)');

/** @type {WeakSet<Element>} */
const initialised = new WeakSet();

class CollectionController {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {number | undefined} */
  #priceTimer;
  /** @type {IntersectionObserver | null} */
  #infinite = null;
  #loadingMore = false;
  #applying = false;

  /** @param {HTMLElement} section */
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId || '';

    section.addEventListener('change', this.#onChange);
    section.addEventListener('submit', this.#onSubmit);
    section.addEventListener('click', this.#onClick);
    // `close` does not bubble; the capture phase still sees it.
    section.addEventListener('close', this.#onPanelClose, true);
    window.addEventListener('popstate', this.#onPopState);

    this.#setupInfinite();
  }

  get connected() {
    return this.section.isConnected;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-korsha-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      this.render(url.toString());
      return;
    }

    const form = target.closest('[data-korsha-facets-form]');
    if (!(form instanceof HTMLFormElement)) return;

    if (this.#isStaged(form)) {
      this.#syncApply(form);
      return;
    }

    window.clearTimeout(this.#priceTimer);
    if (target.type === 'number') {
      // Wait for the shopper to finish typing a price.
      this.#priceTimer = window.setTimeout(() => this.render(this.#urlFromForm(form)), PRICE_DEBOUNCE);
      return;
    }
    this.render(this.#urlFromForm(form));
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-korsha-facets-form]')) {
      event.preventDefault();
      if (this.#isStaged(form)) this.#apply(form);
      else this.render(this.#urlFromForm(form));
    } else if (form.matches('[data-korsha-sort-form]')) {
      event.preventDefault();
      const select = form.querySelector('[data-korsha-sort]');
      const url = new URL(window.location.href);
      if (select instanceof HTMLSelectElement) url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      this.render(url.toString());
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const clearStaged = target.closest('[data-korsha-filters-clear]');
    if (clearStaged) {
      const form = clearStaged.closest('dialog')?.querySelector('[data-korsha-facets-form]');
      if (form instanceof HTMLFormElement) this.#clearForm(form);
      return;
    }

    const facetLink = target.closest('a[data-korsha-facet-link]');
    if (facetLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.render(facetLink.href);
      return;
    }

    const loadMore = target.closest('a[data-korsha-load-more]');
    if (loadMore instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.loadMore(loadMore, true);
    }
  };

  /**
   * True when this form is the panel's and the panel is gathering choices.
   * @param {HTMLFormElement} form
   */
  #isStaged(form) {
    return STAGED.matches && Boolean(form.closest('.korsha-filters--staged'));
  }

  /**
   * How many filters the form currently asks for: each ticked value, and the
   * price range once as a whole — the same count the server shows.
   * @param {HTMLFormElement} form
   */
  #selectionCount(form) {
    const values = form.querySelectorAll('input[type="checkbox"]:checked').length;
    const price = Array.from(form.querySelectorAll('input[type="number"]')).some(
      (input) => input instanceof HTMLInputElement && input.value.trim() !== ''
    );
    return values + (price ? 1 : 0);
  }

  /** @param {HTMLFormElement} form */
  #syncApply(form) {
    const panel = form.closest('dialog');
    if (!panel) return;
    const count = this.#selectionCount(form);
    const counter = panel.querySelector('[data-korsha-apply-count]');
    if (counter) counter.textContent = count > 0 ? ` · ${count}` : '';
    const clear = panel.querySelector('[data-korsha-filters-clear]');
    if (clear instanceof HTMLElement) clear.hidden = count === 0;
  }

  /** @param {HTMLFormElement} form */
  #clearForm(form) {
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
    this.#syncApply(form);
  }

  /**
   * Applies the gathered choices: one render, then the panel closes and the
   * shopper is back at the top of the results — no second tap to dismiss it.
   * @param {HTMLFormElement} form
   */
  async #apply(form) {
    if (this.#applying) return;
    const panel = form.closest('dialog');
    const button = panel?.querySelector('[data-korsha-filters-apply]');
    const label = button?.querySelector('[data-korsha-apply-label]');

    this.#applying = true;
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-busy', 'true');
      if (label && button.dataset.applyingLabel) label.textContent = button.dataset.applyingLabel;
    }

    try {
      await this.render(this.#urlFromForm(form));
    } finally {
      this.#applying = false;
    }

    if (panel instanceof HTMLDialogElement && panel.open) panel.close();
    if (this.section.getBoundingClientRect().top < 0) {
      this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Dismissing the panel without applying puts every choice back to what the
   * page is actually showing (the form's server-rendered defaults).
   * @param {Event} event
   */
  #onPanelClose = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLDialogElement) || this.#applying) return;
    const form = panel.querySelector('[data-korsha-facets-form]');
    if (!(form instanceof HTMLFormElement) || !this.#isStaged(form)) return;
    form.reset();
    this.#syncApply(form);
  };

  #onPopState = () => {
    if (!this.connected) return;
    this.render(window.location.href, { push: false });
  };

  /** @param {HTMLFormElement} form */
  #urlFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (typeof value === 'string' && value !== '') params.append(key, value);
    }
    const url = new URL(form.getAttribute('action') || this.section.dataset.url || window.location.pathname, window.location.origin);
    url.search = params.toString();
    return url.toString();
  }

  /**
   * Re-renders the section for a filter or sort URL.
   * @param {string} href
   * @param {{ push?: boolean }} [options]
   */
  async render(href, { push = true } = {}) {
    const target = new URL(href, window.location.origin);
    target.searchParams.delete('section_id');

    this.#abort?.abort();
    this.#abort = new AbortController();

    if (push && target.toString() !== window.location.href) {
      window.history.pushState({ korsha: true }, '', target.toString());
    }

    const results = this.section.querySelector('[data-korsha-region="results"]');
    results?.setAttribute('aria-busy', 'true');

    const events = await eventsModule;
    const isSearch = this.section.dataset.pageType === 'search';
    const UpdateEvent = isSearch ? events?.SearchUpdateEvent : events?.CollectionUpdateEvent;
    const deferred = UpdateEvent?.createPromise?.();
    if (UpdateEvent && deferred) {
      const productFilters = UpdateEvent.parseProductFilters?.();
      const sortKey = UpdateEvent.getSortKey?.();
      if (isSearch) {
        this.section.dispatchEvent(
          new UpdateEvent({
            search: {
              query: target.searchParams.get('q') ?? '',
              ...(productFilters && { productFilters }),
              sortKey,
            },
            promise: deferred.promise,
          })
        );
      } else {
        const rawId = this.section.dataset.collectionId;
        this.section.dispatchEvent(
          new UpdateEvent({
            collection: {
              id: rawId && /^\d+$/.test(rawId) ? rawId : null,
              handle: this.section.dataset.collectionHandle || '',
              productsCount: Number(this.section.dataset.productsCount) || 0,
            },
            ...(productFilters && { productFilters }),
            sortKey,
            promise: deferred.promise,
          })
        );
      }
    }

    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-korsha-collection]');
      if (!fresh) throw new Error('Collection section missing from response');

      this.#swap(fresh);

      const sort = this.section.querySelector('[data-korsha-sort]');
      const sortValue = target.searchParams.get('sort_by');
      if (sort instanceof HTMLSelectElement && sortValue && sort.value !== sortValue) {
        sort.value = sortValue;
        const label = sort.parentElement?.querySelector('.korsha-select__value');
        if (label) label.textContent = sort.selectedOptions[0]?.textContent?.trim() || '';
      }

      const count = Number(fresh.getAttribute('data-products-count')) || 0;
      this.section.dataset.productsCount = String(count);
      deferred?.resolve(isSearch ? { totalCount: count } : { productsCount: count });

      this.#setupInfinite();

      // Sorting or removing a filter from further down the page brings the
      // shopper back to the top of the results. Never while a panel is open.
      const panelOpen = this.section.querySelector('dialog[open]');
      if (!panelOpen && this.section.getBoundingClientRect().top < 0) {
        this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      deferred?.reject(error);
      console.error('[korsha] collection update failed', error);
      // The URL is the source of truth; a full load always gets it right.
      window.location.assign(target.toString());
      return;
    } finally {
      results?.removeAttribute('aria-busy');
    }
  }

  /** @param {Element} fresh */
  #swap(fresh) {
    const active = document.activeElement;
    const activeId = active instanceof HTMLElement && this.section.contains(active) ? active.id : '';

    for (const name of REGIONS) {
      const current = this.section.querySelector(`[data-korsha-region="${name}"]`);
      const next = fresh.querySelector(`[data-korsha-region="${name}"]`);
      if (!current || !next) continue;

      // Leave a price field alone while it is being typed in.
      if (active instanceof HTMLInputElement && active.type === 'number' && current.contains(active)) continue;

      const openState = new Map(
        Array.from(current.querySelectorAll('details[data-korsha-facet]')).map((details) => [
          details.getAttribute('data-korsha-facet'),
          /** @type {HTMLDetailsElement} */ (details).open,
        ])
      );

      current.innerHTML = next.innerHTML;

      current.querySelectorAll('details[data-korsha-facet]').forEach((details) => {
        const key = details.getAttribute('data-korsha-facet');
        if (key && openState.has(key)) /** @type {HTMLDetailsElement} */ (details).open = Boolean(openState.get(key));
      });
    }

    if (activeId) {
      const restored = document.getElementById(activeId);
      if (restored instanceof HTMLElement && restored !== document.activeElement) restored.focus({ preventScroll: true });
    }
  }

  /**
   * Appends the next page of products.
   * @param {HTMLAnchorElement} link
   * @param {boolean} fromClick
   */
  async loadMore(link, fromClick) {
    if (this.#loadingMore) return;
    this.#loadingMore = true;
    link.setAttribute('aria-busy', 'true');

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-korsha-collection]');
      const grid = this.section.querySelector('[data-korsha-grid]');
      const pagination = this.section.querySelector('[data-korsha-pagination]');
      if (!fresh || !grid) throw new Error('Load more response incomplete');

      /** @type {HTMLElement | null} */
      let firstNew = null;
      fresh.querySelectorAll('[data-korsha-grid-item]').forEach((item) => {
        const node = /** @type {HTMLElement} */ (document.importNode(item, true));
        node.classList.add('is-new');
        grid.append(node);
        firstNew ??= node;
      });

      const freshPagination = fresh.querySelector('[data-korsha-pagination]');
      if (pagination && freshPagination) pagination.innerHTML = freshPagination.innerHTML;

      // A keyboard user who pressed the button continues from the first new
      // product rather than being dropped back at the top of the page.
      if (fromClick && firstNew) {
        /** @type {HTMLElement | null} */ (firstNew.querySelector('.korsha-card__link'))?.focus({ preventScroll: true });
      }
    } catch (error) {
      console.error('[korsha] load more failed', error);
      window.location.assign(link.href);
      return;
    } finally {
      this.#loadingMore = false;
      link.removeAttribute('aria-busy');
    }

    this.#setupInfinite();
  }

  #setupInfinite() {
    this.#infinite?.disconnect();
    this.#infinite = null;

    const wrap = this.section.querySelector('[data-korsha-load-more-wrap][data-mode="infinite"]');
    const link = wrap?.querySelector('a[data-korsha-load-more]');
    if (!(link instanceof HTMLAnchorElement)) return;

    this.#infinite = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore(link, false);
      },
      { rootMargin: '0px 0px 800px 0px' }
    );
    this.#infinite.observe(link);
  }
}

/** @param {ParentNode} root */
function init(root = document) {
  root.querySelectorAll('[data-korsha-collection]').forEach((section) => {
    if (!(section instanceof HTMLElement) || initialised.has(section)) return;
    initialised.add(section);
    new CollectionController(section);
  });
}

init();

// The theme editor swaps a section's markup in place after each edit.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
